import { GRAPH_DEFAULTS, SCALES } from "../Enums/index.js";
import {
	Point,
	TooltipPositionerFunction,
	ChartType,
	ActiveElement,
	InteractionModeFunction,
	Interaction,
	Chart,
	InteractionItem,
	TooltipPosition,
} from "chart.js";
// import {
// 	ChartEvent,
// 	InteractionOptions,
// 	TooltipPosition,
// } from "chart.js/types"; // todo figure out why this errors on my dev
import { getRelativePosition } from "chart.js/helpers";
import { formatNumber } from "../Scales/scaleNumberFormatter.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import { exists, translate as t } from "../i18nBridge.js";
import {
	pushToTable,
	resetTableIndex,
	setCombinedValue,
	setHorizontal,
	setLegend,
	setLimitReachedAt,
	setOffset,
	setVertical,
} from "./tooltipSetters.js";
import { yoyLegendToSortOrder } from "./legendOptions.js";

/**
 * Tooltip documentation.
 *
 * The tooltip is relatively custom. Depending on the type of chart (bar, area, line) we use different interaction modes.
 * For bar the point (intersect) interaction is used.
 * For area we use a custom interaction called nearestArea.
 * For line we use nearest.
 *
 * The positioning is also custom and will be placed close to the cursor.
 */

declare module "chart.js" {
	interface TooltipPositionerMap {
		cursor: TooltipPositionerFunction<ChartType>;
	}
	interface InteractionModeMap {
		nearestArea: InteractionModeFunction;
	}
}

export function setCollisionToNearestArea(
	chart: Chart,
	e: ChartEvent,
	options: InteractionOptions,
	useFinalPosition?: boolean
) {
	const position = getRelativePosition(e, chart);

	// First get the nearest elements from each dataset based on the X axis only.
	// This will result in 1 item per dataset (e.g. capacity and occupancy).
	const nearestXElements = Interaction.modes.nearest(
		chart,
		e,
		{ axis: "x" },
		useFinalPosition
	);

	// Of the nearest X items find the item in which the cursor is placed (for an area graph).
	// For example
	// |            |
	// | P1-------- |
	// |    Cap     |
	// | P2-------- |
	// |    Occ     |
	// | ---------- |
	// P1 is the point for capacity and P2 for occupancy.
	// We find the point where the cursor is more than P.y and only keep the point with the highest Y.
	// This results in when the cursor is placed in the area named CAP, it will find the capacity point.
	// If it is below P2 it will return the point for occupancy.
	const zeroY = chart.scales?.y?.getPixelForValue(0) ?? Infinity;
	const isInNegativeZone = position.y > zeroY;

	let nearestElement: InteractionItem = null;

	nearestXElements.forEach(({ element, datasetIndex, index }) => {
		if (position.y < element.y !== isInNegativeZone) return;
		const isMoreExtreme = isInNegativeZone
			? element.y < nearestElement?.element.y
			: element.y > nearestElement?.element.y;
		if (nearestElement === null || isMoreExtreme) {
			nearestElement = { element, datasetIndex, index };
		}
	});

	return nearestElement ? [nearestElement] : [];
}

export function setTooltipPosition(
	items: ActiveElement[],
	eventPosition: Point
): TooltipPosition {
	if (!items.length) {
		return { x: 0, y: 0 };
	}

	return {
		x: eventPosition.x,
		y: eventPosition.y,
	};
}

export const customTooltip = (
	derivedDocument: DocumentDO,
	isDarkMode: boolean,
	graphType: string
) => {
	let mode = "nearest";
	if (graphType === "bar") {
		mode = "point";
	} else if (graphType === "area") {
		mode = "nearestArea";
	}

	return {
		position: "cursor",
		mode: mode,
		enabled: false,
		external: (context) =>
			externalTooltipHandler(context, derivedDocument, isDarkMode),
		filter: (item) => {
			return item.dataset.yAxisID !== SCALES.Y_SECONDARY;
		},
		callbacks: {
			label(hoverItem) {
				const value =
					typeof hoverItem.raw === "object" ? hoverItem.raw[0] : hoverItem.raw;
				const offset = hoverItem.dataset.stack;
				const legend = hoverItem.dataset.label;

				const yAxisID = hoverItem.dataset.yAxisID;

				const mirrorDirection = hoverItem.dataset.mirrorDirection;
				const type = hoverItem.dataset.type;
				const motivesType = hoverItem.dataset.motivesType;

				const allTotals =
					hoverItem.chart.$totalizer.stacktotal[SCALES.Y][
						`${offset}-${mirrorDirection}-${motivesType}`
					];

				const stackTotal = allTotals ? allTotals[hoverItem.dataIndex] ?? 0 : 0;

				const residualCapacity: number =
					motivesType === "capacity"
						? stackTotal -
						  (hoverItem.chart.$totalizer.stacktotal[SCALES.Y][
								`${offset}-${mirrorDirection}-${hoverItem.dataset.type}`
						  ]?.[hoverItem.dataIndex] ?? 0)
						: null;

				let percentageOfStack: number = value / stackTotal;
				if (!hoverItem.chart.$totalizer.showPercentageOfStack) {
					percentageOfStack = undefined;
				}

				const meta = hoverItem?.dataset?.meta?.[hoverItem.dataIndex];

				return {
					value,
					offset,
					legend,
					percentageOfStack,
					yAxisID,
					type,
					motivesType,
					residualCapacity,
					meta,
				};
			},
		},
	};
};

const getOrCreateTooltip = (chart, isDarkMode, xAlign = "left") => {
	let tooltipEl = chart.canvas.parentNode.querySelector("div");
	let arrow = tooltipEl?.querySelector("div");

	if (!tooltipEl) {
		tooltipEl = document.createElement("div");
		tooltipEl.style.boxShadow = "0px 2px 6px rgba(0, 0, 0, 0.071)";
		tooltipEl.style.borderRadius = "4px";
		tooltipEl.style.fontSize = "12px";
		tooltipEl.style.opacity = 1;
		tooltipEl.style.pointerEvents = "none";
		tooltipEl.style.position = "absolute";
		tooltipEl.style.transition = "all .1s ease";
		tooltipEl.style.padding = "0px";
		tooltipEl.yAlign = "top";

		arrow = document.createElement("div");
		arrow.style.width = "12px";
		arrow.style.height = "12px";
		arrow.style.position = "absolute";

		const table = document.createElement("table");
		table.style.margin = "0px";

		tooltipEl.appendChild(arrow);
		tooltipEl.appendChild(table);
		chart.canvas.parentNode.appendChild(tooltipEl);
	}

	if (xAlign === "right") {
		tooltipEl.style.transform = "translate(-100%, -50%) translateX(-36px)";
		arrow.style.transform = "translate(0, -50%) rotate(45deg)";
		arrow.style.left = null;
		arrow.style.right = "-6px";
		arrow.style.top = "50%";
		arrow.style.bottom = null;
		arrow.style.boxShadow = "2px -2px 3px -3px rgba(0, 0, 0, 0.9)";
	} else if (xAlign === "left") {
		tooltipEl.style.transform = "translate(0, -50%) translateX(36px)";
		arrow.style.transform = "translate(0, -50%) rotate(45deg)";
		arrow.style.left = "-6px";
		arrow.style.right = null;
		arrow.style.top = "50%";
		arrow.style.bottom = null;
		arrow.style.boxShadow = "-2px 2px 3px -3px rgba(0, 0, 0, 0.9)";
	} else if (xAlign === "center") {
		tooltipEl.style.transform = "translate(-50%, -100%) translateY(-36px)";
		arrow.style.transform = "translate(-50%, 0) rotate(45deg)";
		arrow.style.left = "50%";
		arrow.style.right = null;
		arrow.style.top = null;
		arrow.style.bottom = "-6px";
		arrow.style.boxShadow = "2px 2px 3px -3px rgba(0, 0, 0, 0.9)";
	}

	// set colors that could change every display when darkMode is toggled
	tooltipEl.style.background = isDarkMode
		? GRAPH_DEFAULTS.TOOLTIP_BACKGROUND_COLOR_DARK
		: GRAPH_DEFAULTS.TOOLTIP_BACKGROUND_COLOR;

	tooltipEl.style.color = isDarkMode
		? GRAPH_DEFAULTS.TEXT_COLOR_DARK
		: GRAPH_DEFAULTS.TEXT_COLOR;

	arrow.style.background = isDarkMode
		? GRAPH_DEFAULTS.TOOLTIP_BACKGROUND_COLOR_DARK
		: GRAPH_DEFAULTS.TOOLTIP_BACKGROUND_COLOR;

	return tooltipEl;
};

function createTable(): HTMLTableElement {
	const table = document.createElement("table");

	table.style.border = "1px solid rgb(var(--color-background))";
	table.style.borderCollapse = "collapse";
	table.style.display = "block";
	table.style.width = "100%";
	table.style.minWidth = "220px";
	table.style.maxWidth = "600px";
	table.style.overflowX = "hidden";

	return table;
}

function createTableWrapper(
	color: string,
	marginTop: number = 4
): HTMLDivElement {
	const tableWrapper = document.createElement("div");

	tableWrapper.style.borderWidth = "0";
	tableWrapper.style.paddingLeft = "4px";
	tableWrapper.style.borderLeft = `4px solid ${color}`;
	tableWrapper.style.marginTop = `${marginTop}px`;

	return tableWrapper;
}

let openTimeout: number;

const clearOpenTimeout = () => {
	if (openTimeout !== undefined) {
		clearTimeout(openTimeout);
	}
};

const shouldHideTooltip = (tooltip) =>
	tooltip.opacity === 0 || !tooltip.body || !tooltip.body.length;

const hideTooltip = (tooltipEl) => {
	tooltipEl.style.opacity = 0;
};

const prepareTooltip = (tooltip) => {
	tooltip.backgroundColor = "rgb(var(--color-surface))";
};

const getTableRoot = (tooltipEl) => tooltipEl.querySelector("table");

const clearTable = (tableRoot) => {
	while (tableRoot.firstChild) {
		tableRoot.firstChild.remove();
	}
};

const extractDataContext = (context, chart) => {
	const index = context.tooltip.dataPoints[0].dataIndex;
	const datasetIndex = context.tooltip.dataPoints[0].datasetIndex;
	const selectedBar = chart?.data?.datasets[datasetIndex];

	const titleLines = context.tooltip.title || [];
	const bodyLines = context.tooltip.body.map((b) => b.lines);
	const bar = bodyLines[0][0];

	return { index, datasetIndex, selectedBar, titleLines, bar };
};

const getAvailableBars = (chart, selectedBar) =>
	chart?.data?.datasets
		?.filter(
			(d) =>
				d?.stack === selectedBar?.stack &&
				d?.mirrorDirection === selectedBar?.mirrorDirection &&
				(d.yAxisID === "y" || (d.type === "line" && d.yAxisID !== "ySecondary"))
		)
		?.sort((a, b) => a.order - b.order) ?? [];

// Capacity can be split into multiple datasets (capacity_normal / capacity_programmatic), stacked on yCap.
const getCapacityDatasets = (chart, selectedBar) =>
	chart?.data?.datasets
		?.filter(
			(d) =>
				d?.stack === selectedBar?.stack &&
				d?.mirrorDirection === selectedBar?.mirrorDirection &&
				d.yAxisID === "yCap"
		)
		?.sort((a, b) => a.order - b.order) ?? [];

const sumValuesAt = (datasets, index): number =>
	datasets.reduce((acc: number, d) => acc + (d.data[index] ?? 0), 0);

const accumulateValues =
	(index) =>
	(acc: number, d): number => {
		if (d.label === "capacity") {
			return d.data[index];
		}
		return acc + d.data[index];
	};

const calculateValues = (
	chart,
	derivedDocument: DocumentDO,
	selectedBar,
	bar,
	index
) => {
	const capacities = getCapacityDatasets(chart, selectedBar);
	const availableBars = getAvailableBars(chart, selectedBar);

	const capacityIndex = capacities.findIndex((d) => d.label === bar.legend);
	const isCapacity = capacityIndex !== -1;
	const isSplitCapacity = isCapacity && bar.legend !== "capacity";

	const totalValue = capacities.length
		? sumValuesAt(capacities, index)
		: availableBars.reduce(accumulateValues(index), 0);

	let accumulatedValue;

	if (isCapacity) {
		accumulatedValue = sumValuesAt(capacities.slice(0, capacityIndex + 1), index);
	} else {
		let bars = availableBars;

		if (derivedDocument.date_comparison?.showDeltaAs === "primary") {
			const negative = [];
			const positive = [];

			availableBars.forEach((b) => {
				b.data?.[index] < 0 ? negative.push(b) : positive.push(b);
			});
			bars = [...negative.toReversed(), ...positive];
		}
		const barIndex = Math.max(
			0,
			bars.findIndex((b) => b.label === bar.legend) + 1
		);

		accumulatedValue = bars
			.slice(0, barIndex)
			.reduce(accumulateValues(index), 0);
	}

	// A single capacity shows its residual; split capacities show their own share of the stack.
	const currentAmount =
		isCapacity && !isSplitCapacity ? bar.residualCapacity : bar.value;

	return { totalValue, accumulatedValue, currentAmount };
};

/**
 * If delta is shown as secondary vertical, this will always render "Offset | Y". which feels quite redundant
 */
const shouldRenderOffset = (bar, derivedDocument: DocumentDO) =>
	bar?.offset &&
	derivedDocument.offset_dimension?.name_clean &&
	!(
		derivedDocument?.legend_dimension?.family_name_clean === "comparison" &&
		derivedDocument?.date_comparison?.showDeltaAs === "secondary"
	);

const shouldRenderLegend = (derivedDocument: DocumentDO) =>
	derivedDocument?.legend_dimension !== null &&
	JSON.stringify(derivedDocument?.offset_dimension) !==
		JSON.stringify(derivedDocument?.legend_dimension);

const buildMainTable = (dataContext, chart, derivedDocument: DocumentDO) => {
	const { index, selectedBar, titleLines, bar } = dataContext;

	const tableWrapper = createTableWrapper(selectedBar.color, 0);
	const table = createTable();

	const { totalValue, accumulatedValue, currentAmount } = calculateValues(
		chart,
		derivedDocument,
		selectedBar,
		bar,
		index
	);

	resetTableIndex();

	setVertical(table, derivedDocument, bar, currentAmount, totalValue);

	if (currentAmount !== accumulatedValue) {
		setCombinedValue(table, derivedDocument, accumulatedValue, totalValue);
	}

	if (titleLines[0]) {
		setLimitReachedAt(table, derivedDocument, bar?.meta?.limit_reached_at);
		setHorizontal(table, derivedDocument, titleLines[0]);
	}

	if (shouldRenderOffset(bar, derivedDocument)) {
		setOffset(table, derivedDocument, bar);
	}

	if (shouldRenderLegend(derivedDocument)) {
		setLegend(table, derivedDocument, bar);
	}

	tableWrapper.appendChild(table);
	return tableWrapper;
};

const getComparisonPeriod = (dataset) => {
	let stack = dataset?.stack;
	if (typeof stack !== "string") {
		return stack;
	}

	const suffix = `_${dataset?.mirrorDirection}`;
	if (stack.endsWith(suffix)) {
		stack = stack.slice(0, -suffix.length);
	}

	if (stack.startsWith("Δ")) {
		stack = stack.slice(1);
	}

	const match = stack.match(/(?:^|\s)(Y(?:-\d+)?)$/);
	return match ? match[1] : stack;
};

const appendSecondaryVerticals = (
	tableRoot,
	chart,
	index,
	derivedDocument: DocumentDO,
	selectedBar
) => {
	const isDeltaOfSelectedBar = (data) =>
		derivedDocument?.date_comparison?.showDeltaAs === "secondary" &&
		typeof data?.stack === "string" &&
		data.stack.startsWith("Δ");

	chart?.data?.datasets
		?.filter(
			(data) =>
				data.yAxisID === "ySecondary" &&
				(isDeltaOfSelectedBar(data) ||
					getComparisonPeriod(data) === getComparisonPeriod(selectedBar)) &&
				data.data.some((d) => d !== null)
		)
		?.toSorted(
			(a, b) =>
				yoyLegendToSortOrder(a.label, derivedDocument) -
				yoyLegendToSortOrder(b.label, derivedDocument)
		)
		?.forEach((secondaryVertical) => {
			const wrapper = createTableWrapper(secondaryVertical.color);
			const table = createTable();

			const value = secondaryVertical.data[index];
			const valueFormat =
				derivedDocument.date_comparison.showDeltaAs === "secondary" &&
				!derivedDocument.date_comparison.isDeltaAbsolute
					? "percentage"
					: derivedDocument.secondary_vertical.format;
			const formattedValue = formatNumber(value, valueFormat);

			const translationKey = `properties.${secondaryVertical.label}.title`;
			const translation = exists(translationKey)
				? t(translationKey)
				: secondaryVertical.label ?? "Secondary Vertical";

			pushToTable(table, [translation, formattedValue]);

			const limitReachedAt = secondaryVertical?.meta[index]?.limit_reached_at;

			setLimitReachedAt(table, derivedDocument, limitReachedAt);

			wrapper.appendChild(table);
			tableRoot.appendChild(wrapper);
		});
};

const appendMinMaxOccupancy = (
	tableRoot,
	chart,
	index,
	selectedBar,
	derivedDocument
) => {
	chart?.data?.datasets
		.filter(
			(data) =>
				(data.yAxisID === "yMin" || data.yAxisID === "yMax") &&
				data.stack === selectedBar.stack &&
				data.data.some((d) => d !== null)
		)
		.forEach((minMaxOccupancy) => {
			const wrapper = createTableWrapper(minMaxOccupancy.color);
			const table = createTable();

			const value = minMaxOccupancy.data[index];

			const translationKey = `properties.${minMaxOccupancy.label}.title`;
			const translation = exists(translationKey)
				? t(translationKey)
				: `${minMaxOccupancy.yAxisID === "yMin" ? "Min" : "Max"}  Occupancy`;

			const formattedValue = formatNumber(value);

			pushToTable(table, [translation, formattedValue]);

			const limitReachedAt = minMaxOccupancy?.meta[index]?.limit_reached_at;

			setLimitReachedAt(table, derivedDocument, limitReachedAt);

			wrapper.appendChild(table);
			tableRoot.appendChild(wrapper);
		});
};

const positionTooltip = (chart, tooltip, tooltipEl) => {
	const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

	openTimeout = setTimeout(() => {
		tooltipEl.style.opacity = 1;
	}, 200);

	tooltipEl.style.left = positionX + tooltip.caretX + "px";
	tooltipEl.style.top = positionY + tooltip.caretY + "px";
	tooltipEl.style.font = tooltip.options.bodyFont.string;
	tooltipEl.style.padding =
		tooltip.options.padding + "px " + tooltip.options.padding + "px";
};

export const externalTooltipHandler = (
	context,
	derivedDocument: DocumentDO,
	isDarkMode: boolean
) => {
	const { chart, tooltip } = context;
	const tooltipEl = getOrCreateTooltip(chart, isDarkMode, tooltip.xAlign);

	clearOpenTimeout();
	if (shouldHideTooltip(tooltip)) {
		hideTooltip(tooltipEl);
		return;
	}

	prepareTooltip(tooltip);
	const tableRoot = getTableRoot(tooltipEl);
	if (!tableRoot) return;

	clearTable(tableRoot);

	const dataContext = extractDataContext(context, chart);
	const mainTable = buildMainTable(dataContext, chart, derivedDocument);

	tableRoot.appendChild(mainTable);

	appendMinMaxOccupancy(
		tableRoot,
		chart,
		dataContext.index,
		dataContext.selectedBar,
		derivedDocument
	);

	appendSecondaryVerticals(
		tableRoot,
		chart,
		dataContext.index,
		derivedDocument,
		dataContext.selectedBar
	);

	positionTooltip(chart, tooltip, tooltipEl);
};
